import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CareLetter from './CareLetter';
import { uploadFields } from '../content/careLetter';
import { WEB3FORMS_ACCESS_KEY } from '../config';
import './ClientForm.css';

const TITLE_OPTIONS = ['Mr', 'Mrs', 'Miss', 'Ms', 'Dr', 'Other'];

const EMPTY_ATTORNEY = { fullName: '', dob: '', address: '', postcode: '', email: '', relationship: '' };
const EMPTY_REPLACEMENT = { fullName: '', dob: '', address: '', postcode: '', relationship: '' };
const EMPTY_NOTIFY = { fullName: '', address: '', postcode: '', relationship: '' };

const ClientForm = () => {
  const [formData, setFormData] = useState({
    // Section 1 - Donor
    title: '',
    titleOther: '',
    firstName: '',
    middleName: '',
    lastName: '',
    otherNames: '',
    dob: '',
    address: '',
    postcode: '',
    email: '',
    phone: '',
    // Section 2
    lpaType: '',
    // Section 3
    attorneys: [{ ...EMPTY_ATTORNEY }, { ...EMPTY_ATTORNEY }, { ...EMPTY_ATTORNEY }, { ...EMPTY_ATTORNEY }],
    // Section 4
    decisionMethod: '',
    decisionExplanation: '',
    // Section 5
    wantReplacements: '',
    replacements: [{ ...EMPTY_REPLACEMENT }, { ...EMPTY_REPLACEMENT }],
    // Section 6
    financialTiming: '',
    // Section 7
    lifeSustaining: '',
    // Section 8
    notifyPeople: [{ ...EMPTY_NOTIFY }, { ...EMPTY_NOTIFY }, { ...EMPTY_NOTIFY }],
    // Section 9
    preferences: '',
    // Section 10
    instructions: '',
    // Section 11
    needCertificateProvider: false,
    certProviderName: '',
    certProviderAddress: '',
    certProviderPostcode: '',
    certProviderProfession: '',
    certProviderKnownYears: '',
    // Section 12
    sendLpaTo: '',
    sendLpaOtherName: '',
    sendLpaOtherCompany: '',
    sendLpaOtherAddress: '',
    sendLpaOtherPostcode: '',
    // Section 15
    paymentMethod: '',
  });

  // Set once the client accepts the Client Care Letter & Terms of Engagement.
  // Until then the letter is shown in place of the form, and its contents are
  // sent with the form submission so we have a record of what was accepted.
  const [careLetter, setCareLetter] = useState(null);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const navigate = useNavigate();

  // The care letter is long, so clients accept it near the bottom of the page.
  // ScrollToTop only fires on a path change and the path is unchanged here, so
  // reset the scroll ourselves as the form takes over.
  useEffect(() => {
    if (careLetter) {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }
  }, [careLetter]);

  const updateField = useCallback((name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  }, []);

  const updateArrayItem = useCallback((arrayName, index, field, value) => {
    setFormData(prev => {
      const arr = [...prev[arrayName]];
      arr[index] = { ...arr[index], [field]: value };
      return { ...prev, [arrayName]: arr };
    });
  }, []);

  const formatAttorneys = (attorneys) => {
    return attorneys
      .filter(a => a.fullName.trim())
      .map((a, i) => (
        `Attorney ${i + 1}: ${a.fullName}, DOB: ${a.dob}, Address: ${a.address}, ${a.postcode}, Email: ${a.email}, Relationship: ${a.relationship}`
      )).join('\n');
  };

  const formatReplacements = (replacements) => {
    return replacements
      .filter(r => r.fullName.trim())
      .map((r, i) => (
        `Replacement ${i + 1}: ${r.fullName}, DOB: ${r.dob}, Address: ${r.address}, ${r.postcode}, Relationship: ${r.relationship}`
      )).join('\n');
  };

  const formatNotify = (people) => {
    return people
      .filter(p => p.fullName.trim())
      .map((p, i) => (
        `Person ${i + 1}: ${p.fullName}, Address: ${p.address}, ${p.postcode}, Relationship: ${p.relationship}`
      )).join('\n');
  };

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setSubmitError('');

    if (!formData.firstName.trim() || !formData.lastName.trim() || !formData.email.trim()) {
      setSubmitError('Please fill in at least your first name, last name, and email.');
      return;
    }

    setSubmitting(true);

    try {
      const title = formData.title === 'Other' ? formData.titleOther : formData.title;
      const fullName = `${title} ${formData.firstName.trim()} ${formData.middleName.trim()} ${formData.lastName.trim()}`.replace(/\s+/g, ' ').trim();

      const lpaTypeLabels = {
        'property-financial': 'Property & Financial Affairs',
        'health-welfare': 'Health & Welfare',
        'both': 'Both',
      };

      const decisionLabels = {
        'jointly-severally': 'Jointly and severally',
        'jointly': 'Jointly',
        'mixed': 'Jointly for some, jointly & severally for others',
      };

      const body = [
        `=== CLIENT CARE - TERMS OF BUSINESS ===`,
        `Accepted: Yes`,
        `Clients Full Name(s): ${careLetter.clientNames}`,
        `Name of person signing: ${careLetter.signatoryName}`,
        `Signature (typed): ${careLetter.signature}`,
        `Date: ${careLetter.dateDisplay}`,
        `Early start during cooling-off period requested: ${careLetter.earlyStart ? 'Yes' : 'No'}`,
        `Terms version: ${careLetter.version}`,
        '',
        `=== IDENTITY DOCUMENTS ===`,
        ...uploadFields.map(field => {
          const chosen = careLetter.files?.[field.key];
          return chosen
            ? `${field.emailLabel}: ${chosen.file.name} (attached)`
            : `${field.emailLabel}: not provided - client to send by email or WhatsApp`;
        }),
        '',
        `=== DONOR DETAILS ===`,
        `Name: ${fullName}`,
        formData.otherNames ? `Other names: ${formData.otherNames}` : '',
        `DOB: ${formData.dob}`,
        `Address: ${formData.address}, ${formData.postcode}`,
        `Email: ${formData.email}`,
        `Phone: ${formData.phone}`,
        '',
        `=== LPA TYPE ===`,
        lpaTypeLabels[formData.lpaType] || formData.lpaType,
        '',
        `=== ATTORNEYS ===`,
        formatAttorneys(formData.attorneys),
        '',
        `=== DECISION METHOD ===`,
        decisionLabels[formData.decisionMethod] || formData.decisionMethod,
        formData.decisionMethod === 'mixed' ? `Explanation: ${formData.decisionExplanation}` : '',
        '',
        `=== REPLACEMENT ATTORNEYS ===`,
        `Wants replacements: ${formData.wantReplacements}`,
        formData.wantReplacements === 'yes' ? formatReplacements(formData.replacements) : '',
        '',
        formData.lpaType !== 'health-welfare' ? `=== PROPERTY & FINANCIAL TIMING ===\n${formData.financialTiming === 'registered' ? 'As soon as the LPA is registered' : 'Only if I lose mental capacity'}\n` : '',
        formData.lpaType !== 'property-financial' ? `=== LIFE-SUSTAINING TREATMENT ===\n${formData.lifeSustaining === 'yes' ? 'Yes - attorneys can consent or refuse' : 'No - doctors will decide'}\n` : '',
        `=== PEOPLE TO NOTIFY ===`,
        formatNotify(formData.notifyPeople),
        '',
        `=== PREFERENCES ===`,
        formData.preferences || '(None)',
        '',
        `=== INSTRUCTIONS ===`,
        formData.instructions || '(None)',
        '',
        `=== CERTIFICATE PROVIDER ===`,
        formData.needCertificateProvider ? 'Client needs us to be certificate provider' : '',
        formData.certProviderName ? `Name: ${formData.certProviderName}` : '',
        formData.certProviderAddress ? `Address: ${formData.certProviderAddress}, ${formData.certProviderPostcode}` : '',
        formData.certProviderProfession ? `Profession: ${formData.certProviderProfession}` : '',
        formData.certProviderKnownYears ? `Known for: ${formData.certProviderKnownYears}` : '',
        '',
        `=== SEND REGISTERED LPA TO ===`,
        formData.sendLpaTo === 'donor' ? 'Donor' : formData.sendLpaTo === 'attorney' ? 'An attorney' : formData.sendLpaTo === 'company' ? 'Kept by The LPA Company' : '',
        formData.sendLpaTo === 'other' ? `Other: ${formData.sendLpaOtherName}, ${formData.sendLpaOtherCompany}, ${formData.sendLpaOtherAddress}, ${formData.sendLpaOtherPostcode}` : '',
        '',
        `=== PAYMENT METHOD ===`,
        formData.paymentMethod === 'card' ? 'Card - Invoice by email with card payment link' : 'Bank Transfer - 50% on instruction, balance on signing/submission',
      ].filter(Boolean).join('\n');

      // Sent as multipart rather than JSON so the identity documents from
      // clause 2 can ride along as Web3Forms attachments. Do not set a
      // Content-Type header -- the browser has to add the multipart boundary.
      const payload = new FormData();
      payload.append('access_key', WEB3FORMS_ACCESS_KEY);
      payload.append('subject', `New LPA Client Form - ${fullName}`);
      payload.append('from_name', 'The LPA Company');
      payload.append('name', fullName);
      payload.append('email', formData.email.trim());
      payload.append('phone', formData.phone.trim());
      payload.append('message', body);

      uploadFields.forEach(field => {
        const chosen = careLetter.files?.[field.key];
        if (chosen) payload.append(field.key, chosen.file, chosen.file.name);
      });

      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        body: payload,
      });

      const data = await response.json();
      if (!data.success) {
        // Web3Forms explains the real cause here (bad key, domain restriction,
        // attachment too large). Keep it out of the client's way but in the
        // console, or the failure is undiagnosable.
        // eslint-disable-next-line no-console
        console.error('Web3Forms rejected the submission:', data.message || data);
        throw new Error('Failed to submit form. Please try again.');
      }
      const submissionId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      navigate('/thank-you', { state: { fromForm: true, submissionId } });
    } catch (err) {
      setSubmitError(err.message || 'Something went wrong. Please try again later.');
    } finally {
      setSubmitting(false);
    }
  }, [formData, careLetter, navigate]);

  const showFinancial = formData.lpaType === 'property-financial' || formData.lpaType === 'both';
  const showHealth = formData.lpaType === 'health-welfare' || formData.lpaType === 'both';

  // The care letter gates the form: no client reaches the questions without
  // first accepting the terms of engagement.
  if (!careLetter) {
    return <CareLetter onAccept={setCareLetter} />;
  }

  return (
    <div className="client-form-page">
      <div className="client-form-container">
        <div className="cf-header">
          <h1 className="cf-header__title">Lasting Power of Attorney</h1>
          <h2 className="cf-header__subtitle">Client Information Form</h2>
          <p className="cf-header__intro">
            Thank you for choosing us to help you prepare your <em>Lasting Power of Attorney (LPA)</em>.
            Please complete the information below as accurately as possible. This will allow us to prepare your application correctly.
          </p>
        </div>

        <form className="cf-form" onSubmit={handleSubmit} noValidate>

          {/* Section 1 - Donor Details */}
          <section className="cf-section">
            <div className="cf-section__header">
              <span className="cf-section__number">1</span>
              <div>
                <h3 className="cf-section__title">Donor Details</h3>
                <p className="cf-section__desc">The person making the LPA</p>
              </div>
            </div>

            <div className="cf-field">
              <label className="cf-label">Title</label>
              <div className="cf-radio-row">
                {TITLE_OPTIONS.map(t => (
                  <button
                    key={t} type="button"
                    className={`cf-chip${formData.title === t ? ' cf-chip--active' : ''}`}
                    onClick={() => updateField('title', t)}
                  >{t}</button>
                ))}
              </div>
              {formData.title === 'Other' && (
                <input className="cf-input cf-input--sm" placeholder="Please specify" value={formData.titleOther} onChange={e => updateField('titleOther', e.target.value)} />
              )}
            </div>

            <div className="cf-row">
              <div className="cf-field">
                <label className="cf-label">Full first name <span className="cf-required">*</span></label>
                <input className="cf-input" value={formData.firstName} onChange={e => updateField('firstName', e.target.value)} />
              </div>
              <div className="cf-field">
                <label className="cf-label">Middle name</label>
                <input className="cf-input" value={formData.middleName} onChange={e => updateField('middleName', e.target.value)} />
              </div>
            </div>

            <div className="cf-row">
              <div className="cf-field">
                <label className="cf-label">Last name <span className="cf-required">*</span></label>
                <input className="cf-input" value={formData.lastName} onChange={e => updateField('lastName', e.target.value)} />
              </div>
              <div className="cf-field">
                <label className="cf-label">Other names <span className="cf-optional">(e.g. maiden name)</span></label>
                <input className="cf-input" value={formData.otherNames} onChange={e => updateField('otherNames', e.target.value)} />
              </div>
            </div>

            <div className="cf-field">
              <label className="cf-label">Date of birth</label>
              <input className="cf-input cf-input--sm" type="date" value={formData.dob} onChange={e => updateField('dob', e.target.value)} />
            </div>

            <div className="cf-field">
              <label className="cf-label">Full address</label>
              <textarea className="cf-textarea" rows="2" value={formData.address} onChange={e => updateField('address', e.target.value)} />
            </div>

            <div className="cf-row">
              <div className="cf-field">
                <label className="cf-label">Postcode</label>
                <input className="cf-input" value={formData.postcode} onChange={e => updateField('postcode', e.target.value)} />
              </div>
              <div className="cf-field">
                <label className="cf-label">Email address <span className="cf-required">*</span></label>
                <input className="cf-input" type="email" value={formData.email} onChange={e => updateField('email', e.target.value)} />
              </div>
            </div>

            <div className="cf-field">
              <label className="cf-label">Phone number</label>
              <input className="cf-input cf-input--sm" type="tel" value={formData.phone} onChange={e => updateField('phone', e.target.value)} />
            </div>
          </section>

          {/* Section 2a - LPA Type */}
          <section className="cf-section">
            <div className="cf-section__header">
              <span className="cf-section__number">2a</span>
              <div>
                <h3 className="cf-section__title">Type of LPA</h3>
                <p className="cf-section__desc">Please select the type you want to create</p>
              </div>
            </div>

            <div className="cf-options">
              {[
                { value: 'property-financial', label: 'Property & Financial Affairs' },
                { value: 'health-welfare', label: 'Health & Welfare' },
                { value: 'both', label: 'Both' },
              ].map(opt => (
                <button
                  key={opt.value} type="button"
                  className={`cf-option${formData.lpaType === opt.value ? ' cf-option--active' : ''}`}
                  onClick={() => updateField('lpaType', opt.value)}
                >
                  <span className="cf-option__radio">
                    {formData.lpaType === opt.value && <span className="cf-option__dot" />}
                  </span>
                  {opt.label}
                </button>
              ))}
            </div>
          </section>

          {/* Section 2b - LPA-specific decisions */}
          {(showFinancial || showHealth) && (
            <section className="cf-section">
              <div className="cf-section__header">
                <span className="cf-section__number">2b</span>
                <div>
                  <h3 className="cf-section__title">LPA Decisions</h3>
                  <p className="cf-section__desc">Tell us how you want each LPA to work.</p>
                </div>
              </div>

              {showFinancial && (
                <div className="cf-sub-section">
                  <h4 className="cf-sub-title">Property &amp; Financial Affairs LPA</h4>
                  <p className="cf-section__desc" style={{ marginBottom: 12 }}>
                    When do you want your attorneys to be able to make decisions? (mark one only)
                  </p>
                  <div className="cf-options">
                    <button type="button" className={`cf-option${formData.financialTiming === 'registered' ? ' cf-option--active' : ''}`} onClick={() => updateField('financialTiming', 'registered')}>
                      <span className="cf-option__radio">{formData.financialTiming === 'registered' && <span className="cf-option__dot" />}</span>
                      <span>
                        <strong>Option A &mdash; As soon as my LPA has been registered</strong> (and also when I don&rsquo;t have mental capacity)
                        <span className="cf-option__desc">
                          Most people choose this option because it is the most practical. While you still have mental capacity, your attorneys can only act with your consent. If you later lose capacity, they can continue to act on your behalf for all decisions covered by this LPA.
                          <br /><br />
                          This option is useful if you are able to make your own decisions but there&rsquo;s another reason you want your attorneys to help you &ndash; for example, if you&rsquo;re away on holiday, or if you have a physical condition that makes it difficult to visit the bank, talk on the phone or sign documents.
                        </span>
                      </span>
                    </button>
                    <button type="button" className={`cf-option${formData.financialTiming === 'incapacity' ? ' cf-option--active' : ''}`} onClick={() => updateField('financialTiming', 'incapacity')}>
                      <span className="cf-option__radio">{formData.financialTiming === 'incapacity' && <span className="cf-option__dot" />}</span>
                      <span>
                        <strong>Option B &mdash; Only when I don&rsquo;t have mental capacity</strong>
                        <span className="cf-option__desc">
                          Be careful &ndash; this can make your LPA a lot less useful. Your attorneys might be asked to prove you do not have mental capacity each time they try to use this LPA.
                        </span>
                      </span>
                    </button>
                  </div>
                </div>
              )}

              {showHealth && (
                <div className="cf-sub-section">
                  <h4 className="cf-sub-title">Health &amp; Welfare LPA</h4>
                  <p className="cf-section__desc" style={{ marginBottom: 12 }}>
                    Life-sustaining treatment decisions (mark one only)
                  </p>
                  <div className="cf-options">
                    <button type="button" className={`cf-option${formData.lifeSustaining === 'yes' ? ' cf-option--active' : ''}`} onClick={() => updateField('lifeSustaining', 'yes')}>
                      <span className="cf-option__radio">{formData.lifeSustaining === 'yes' && <span className="cf-option__dot" />}</span>
                      <span>
                        <strong>Option A &mdash; I give my attorneys authority to give or refuse consent to life-sustaining treatment on my behalf.</strong>
                        <span className="cf-option__desc">
                          If you choose this option, your attorneys can speak to doctors on your behalf as if they were you.
                        </span>
                      </span>
                    </button>
                    <button type="button" className={`cf-option${formData.lifeSustaining === 'no' ? ' cf-option--active' : ''}`} onClick={() => updateField('lifeSustaining', 'no')}>
                      <span className="cf-option__radio">{formData.lifeSustaining === 'no' && <span className="cf-option__dot" />}</span>
                      <span>
                        <strong>Option B &mdash; I do not give my attorneys authority to give or refuse consent to life-sustaining treatment on my behalf.</strong>
                        <span className="cf-option__desc">
                          If you choose this option, your doctors will take into account the views of the attorneys and of people who are interested in your welfare as well as any written statement you may have made, where it is practical and appropriate.
                        </span>
                      </span>
                    </button>
                  </div>
                </div>
              )}
            </section>
          )}

          {/* Section 3 - Attorneys */}
          <section className="cf-section">
            <div className="cf-section__header">
              <span className="cf-section__number">3</span>
              <div>
                <h3 className="cf-section__title">Attorneys</h3>
                <p className="cf-section__desc">The people who will make decisions for you. You must appoint at least one attorney.</p>
              </div>
            </div>

            {formData.attorneys.map((att, i) => (
              <div key={i} className="cf-sub-section">
                <h4 className="cf-sub-title">Attorney {i + 1} {i > 0 && <span className="cf-optional">(optional)</span>}</h4>
                <div className="cf-row">
                  <div className="cf-field">
                    <label className="cf-label">Full name</label>
                    <input className="cf-input" value={att.fullName} onChange={e => updateArrayItem('attorneys', i, 'fullName', e.target.value)} />
                  </div>
                  <div className="cf-field">
                    <label className="cf-label">Date of birth</label>
                    <input className="cf-input" type="date" value={att.dob} onChange={e => updateArrayItem('attorneys', i, 'dob', e.target.value)} />
                  </div>
                </div>
                <div className="cf-field">
                  <label className="cf-label">Address</label>
                  <input className="cf-input" value={att.address} onChange={e => updateArrayItem('attorneys', i, 'address', e.target.value)} />
                </div>
                <div className="cf-row">
                  <div className="cf-field">
                    <label className="cf-label">Postcode</label>
                    <input className="cf-input" value={att.postcode} onChange={e => updateArrayItem('attorneys', i, 'postcode', e.target.value)} />
                  </div>
                  <div className="cf-field">
                    <label className="cf-label">Email address</label>
                    <input className="cf-input" type="email" value={att.email} onChange={e => updateArrayItem('attorneys', i, 'email', e.target.value)} />
                  </div>
                </div>
                <div className="cf-field">
                  <label className="cf-label">Relationship to you</label>
                  <input className="cf-input cf-input--sm" value={att.relationship} onChange={e => updateArrayItem('attorneys', i, 'relationship', e.target.value)} />
                </div>
              </div>
            ))}
          </section>

          {/* Section 4 - Decision Method */}
          <section className="cf-section">
            <div className="cf-section__header">
              <span className="cf-section__number">4</span>
              <div>
                <h3 className="cf-section__title">How Should Your Attorneys Make Decisions?</h3>
              </div>
            </div>

            <div className="cf-options">
              {[
                { value: 'jointly-severally', label: 'Jointly and severally', desc: 'Attorneys can make decisions together or individually' },
                { value: 'jointly', label: 'Jointly', desc: 'All attorneys must agree on every decision' },
                { value: 'mixed', label: 'Jointly for some decisions and jointly & severally for others' },
              ].map(opt => (
                <button
                  key={opt.value} type="button"
                  className={`cf-option${formData.decisionMethod === opt.value ? ' cf-option--active' : ''}`}
                  onClick={() => updateField('decisionMethod', opt.value)}
                >
                  <span className="cf-option__radio">
                    {formData.decisionMethod === opt.value && <span className="cf-option__dot" />}
                  </span>
                  <span>
                    {opt.label}
                    {opt.desc && <span className="cf-option__desc">{opt.desc}</span>}
                  </span>
                </button>
              ))}
            </div>

            {formData.decisionMethod === 'mixed' && (
              <div className="cf-field" style={{ marginTop: 16 }}>
                <label className="cf-label">Please explain which decisions should be joint and which jointly & severally:</label>
                <textarea className="cf-textarea" rows="3" value={formData.decisionExplanation} onChange={e => updateField('decisionExplanation', e.target.value)} />
              </div>
            )}
          </section>

          {/* Section 5 - Replacement Attorneys */}
          <section className="cf-section">
            <div className="cf-section__header">
              <span className="cf-section__number">5</span>
              <div>
                <h3 className="cf-section__title">Replacement Attorneys</h3>
                <p className="cf-section__desc">Replacement attorneys step in if your original attorneys can no longer act.</p>
              </div>
            </div>

            <div className="cf-field">
              <label className="cf-label">Would you like to appoint replacement attorneys?</label>
              <div className="cf-radio-row">
                <button type="button" className={`cf-chip${formData.wantReplacements === 'yes' ? ' cf-chip--active' : ''}`} onClick={() => updateField('wantReplacements', 'yes')}>Yes</button>
                <button type="button" className={`cf-chip${formData.wantReplacements === 'no' ? ' cf-chip--active' : ''}`} onClick={() => updateField('wantReplacements', 'no')}>No</button>
              </div>
            </div>

            {formData.wantReplacements === 'yes' && formData.replacements.map((rep, i) => (
              <div key={i} className="cf-sub-section">
                <h4 className="cf-sub-title">Replacement Attorney {i + 1}</h4>
                <div className="cf-row">
                  <div className="cf-field">
                    <label className="cf-label">Full name</label>
                    <input className="cf-input" value={rep.fullName} onChange={e => updateArrayItem('replacements', i, 'fullName', e.target.value)} />
                  </div>
                  <div className="cf-field">
                    <label className="cf-label">Date of birth</label>
                    <input className="cf-input" type="date" value={rep.dob} onChange={e => updateArrayItem('replacements', i, 'dob', e.target.value)} />
                  </div>
                </div>
                <div className="cf-field">
                  <label className="cf-label">Address</label>
                  <input className="cf-input" value={rep.address} onChange={e => updateArrayItem('replacements', i, 'address', e.target.value)} />
                </div>
                <div className="cf-row">
                  <div className="cf-field">
                    <label className="cf-label">Postcode</label>
                    <input className="cf-input" value={rep.postcode} onChange={e => updateArrayItem('replacements', i, 'postcode', e.target.value)} />
                  </div>
                  <div className="cf-field">
                    <label className="cf-label">Relationship to you</label>
                    <input className="cf-input" value={rep.relationship} onChange={e => updateArrayItem('replacements', i, 'relationship', e.target.value)} />
                  </div>
                </div>
              </div>
            ))}
          </section>

          {/* Section 6 - People to Notify */}
          <section className="cf-section">
            <div className="cf-section__header">
              <span className="cf-section__number">6</span>
              <div>
                <h3 className="cf-section__title">People to Notify <span className="cf-optional">(Optional)</span></h3>
                <p className="cf-section__desc">
                  These people will be notified when your LPA is registered. It can help to inform your GP, close family members, financial advisers or care providers.
                </p>
              </div>
            </div>

            {formData.notifyPeople.map((person, i) => (
              <div key={i} className="cf-sub-section">
                <h4 className="cf-sub-title">Person {i + 1} {i > 1 && <span className="cf-optional">(optional)</span>}</h4>
                <div className="cf-row">
                  <div className="cf-field">
                    <label className="cf-label">Full name</label>
                    <input className="cf-input" value={person.fullName} onChange={e => updateArrayItem('notifyPeople', i, 'fullName', e.target.value)} />
                  </div>
                  <div className="cf-field">
                    <label className="cf-label">Relationship to you</label>
                    <input className="cf-input" value={person.relationship} onChange={e => updateArrayItem('notifyPeople', i, 'relationship', e.target.value)} />
                  </div>
                </div>
                <div className="cf-field">
                  <label className="cf-label">Address</label>
                  <input className="cf-input" value={person.address} onChange={e => updateArrayItem('notifyPeople', i, 'address', e.target.value)} />
                </div>
                <div className="cf-field">
                  <label className="cf-label">Postcode</label>
                  <input className="cf-input cf-input--sm" value={person.postcode} onChange={e => updateArrayItem('notifyPeople', i, 'postcode', e.target.value)} />
                </div>
              </div>
            ))}
          </section>

          {/* Section 7 - Preferences */}
          <section className="cf-section">
            <div className="cf-section__header">
              <span className="cf-section__number">7</span>
              <div>
                <h3 className="cf-section__title">Preferences <span className="cf-optional">(Optional)</span></h3>
                <p className="cf-section__desc">Things you would like your attorneys to consider when making decisions (e.g. preferred care arrangements, living arrangements, financial preferences).</p>
              </div>
            </div>
            <div className="cf-field">
              <textarea className="cf-textarea" rows="4" placeholder="Write any preferences here..." value={formData.preferences} onChange={e => updateField('preferences', e.target.value)} />
            </div>
          </section>

          {/* Section 8 - Instructions */}
          <section className="cf-section">
            <div className="cf-section__header">
              <span className="cf-section__number">8</span>
              <div>
                <h3 className="cf-section__title">Instructions <span className="cf-optional">(Optional)</span></h3>
                <p className="cf-section__desc">Rules your attorneys must follow. For example: &ldquo;My house must not be sold unless necessary for care costs.&rdquo;</p>
              </div>
            </div>
            <div className="cf-field">
              <textarea className="cf-textarea" rows="4" placeholder="Write any instructions here..." value={formData.instructions} onChange={e => updateField('instructions', e.target.value)} />
            </div>
          </section>

          {/* Section 9 - Certificate Provider */}
          <section className="cf-section">
            <div className="cf-section__header">
              <span className="cf-section__number">9</span>
              <div>
                <h3 className="cf-section__title">Certificate Provider</h3>
                <p className="cf-section__desc">
                  This person confirms that you understand the LPA and are not being pressured.
                  They must either have known you for at least 2 years, or be a professional (doctor, solicitor, etc.).
                </p>
              </div>
            </div>

            <div className="cf-field">
              <label className="cf-checkbox-label">
                <input
                  type="checkbox"
                  className="cf-checkbox"
                  checked={formData.needCertificateProvider}
                  onChange={e => updateField('needCertificateProvider', e.target.checked)}
                />
                <span>I need The LPA Company to be my certificate provider (Fees apply)</span>
              </label>
            </div>

            <div className="cf-row">
              <div className="cf-field">
                <label className="cf-label">Full name</label>
                <input className="cf-input" value={formData.certProviderName} onChange={e => updateField('certProviderName', e.target.value)} />
              </div>
              <div className="cf-field">
                <label className="cf-label">Profession <span className="cf-optional">(if applicable)</span></label>
                <input className="cf-input" value={formData.certProviderProfession} onChange={e => updateField('certProviderProfession', e.target.value)} />
              </div>
            </div>

            <div className="cf-field">
              <label className="cf-label">Address</label>
              <input className="cf-input" value={formData.certProviderAddress} onChange={e => updateField('certProviderAddress', e.target.value)} />
            </div>

            <div className="cf-row">
              <div className="cf-field">
                <label className="cf-label">Postcode</label>
                <input className="cf-input" value={formData.certProviderPostcode} onChange={e => updateField('certProviderPostcode', e.target.value)} />
              </div>
              <div className="cf-field">
                <label className="cf-label">How long have they known you?</label>
                <input className="cf-input" value={formData.certProviderKnownYears} onChange={e => updateField('certProviderKnownYears', e.target.value)} />
              </div>
            </div>
          </section>

          {/* Section 10 - Where to send registered LPA */}
          <section className="cf-section">
            <div className="cf-section__header">
              <span className="cf-section__number">10</span>
              <div>
                <h3 className="cf-section__title">Where Should the Registered LPA Be Sent?</h3>
              </div>
            </div>

            <div className="cf-options">
              {[
                { value: 'donor', label: 'Donor' },
                { value: 'attorney', label: 'An attorney' },
                { value: 'company', label: 'Kept by The LPA Company' },
              ].map(opt => (
                <button key={opt.value} type="button" className={`cf-option${formData.sendLpaTo === opt.value ? ' cf-option--active' : ''}`} onClick={() => updateField('sendLpaTo', opt.value)}>
                  <span className="cf-option__radio">{formData.sendLpaTo === opt.value && <span className="cf-option__dot" />}</span>
                  {opt.label}
                </button>
              ))}
            </div>

            {formData.sendLpaTo === 'other' && (
              <div style={{ marginTop: 16 }}>
                <div className="cf-row">
                  <div className="cf-field">
                    <label className="cf-label">Full name</label>
                    <input className="cf-input" value={formData.sendLpaOtherName} onChange={e => updateField('sendLpaOtherName', e.target.value)} />
                  </div>
                  <div className="cf-field">
                    <label className="cf-label">Company <span className="cf-optional">(if applicable)</span></label>
                    <input className="cf-input" value={formData.sendLpaOtherCompany} onChange={e => updateField('sendLpaOtherCompany', e.target.value)} />
                  </div>
                </div>
                <div className="cf-field">
                  <label className="cf-label">Address</label>
                  <input className="cf-input" value={formData.sendLpaOtherAddress} onChange={e => updateField('sendLpaOtherAddress', e.target.value)} />
                </div>
                <div className="cf-field">
                  <label className="cf-label">Postcode</label>
                  <input className="cf-input cf-input--sm" value={formData.sendLpaOtherPostcode} onChange={e => updateField('sendLpaOtherPostcode', e.target.value)} />
                </div>
              </div>
            )}
          </section>

          {/* Section 11 - Application Fee */}
          <section className="cf-section">
            <div className="cf-section__header">
              <span className="cf-section__number">11</span>
              <div>
                <h3 className="cf-section__title">Application Fee</h3>
                <p className="cf-section__desc">How would you like the registration fee to be paid?</p>
              </div>
            </div>

            <div className="cf-options">
              <button type="button" className={`cf-option${formData.paymentMethod === 'card' ? ' cf-option--active' : ''}`} onClick={() => updateField('paymentMethod', 'card')}>
                <span className="cf-option__radio">{formData.paymentMethod === 'card' && <span className="cf-option__dot" />}</span>
                <span>
                  Card
                  <span className="cf-option__desc">We will issue an invoice by email with a card payment link</span>
                </span>
              </button>
              <button type="button" className={`cf-option${formData.paymentMethod === 'transfer' ? ' cf-option--active' : ''}`} onClick={() => updateField('paymentMethod', 'transfer')}>
                <span className="cf-option__radio">{formData.paymentMethod === 'transfer' && <span className="cf-option__dot" />}</span>
                <span>
                  Bank Transfer
                  <span className="cf-option__desc">50% payable on instruction. Balance and OPG fees payable when documents are ready for signing and submission.</span>
                </span>
              </button>
            </div>
          </section>

          {submitError && (
            <div className="cf-error" role="alert">{submitError}</div>
          )}

          <button type="submit" className="cf-submit" disabled={submitting}>
            {submitting ? (
              <>
                <span className="cf-spinner" aria-hidden="true" />
                Submitting...
              </>
            ) : (
              'Submit Information'
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ClientForm;
